///////////////
// IMPORTS
///////////////
// libraries
// constants
import { outlookPaths, paths } from "@clockwise/client-commons/src/constants/site";
// util
import { Null } from "@clockwise/web-commons/src/components/null";
import {
  appPaths,
  pagePaths,
  webappPathRoot,
} from "@clockwise/web-commons/src/constants/route.constants";
import { Redirect, Route, makeRouteConfig } from "found";
import * as React from "react";
// components
import { MicrosoftAuthRedirect } from "#webapp/src/auth/MicrosoftAuthRedirect";
import { HomeUninstall } from "#webapp/src/components/home-uninstall";
import { HomeWrapper } from "#webapp/src/components/home-wrapper";
import { LoginWrapper } from "#webapp/src/components/login-wrapper";
import { ModernWrapperPage } from "#webapp/src/components/modern-wrapper";
import { UnsubscribeWrapperPage } from "#webapp/src/components/unsubscribe-wrapper";

// constants
// util
import { requireLoginOutlook, requireNoAuthApp } from "#webapp/src/util/auth.util";
import { m365NonceUrlParam } from "../auth/auth.util";
import { AppNav } from "../components/app-nav/AppNav";
import { OutlookEventRouter } from "../components/outlook-event-router/OutlookEventRouter";
import { OutlookExistingEvent } from "../components/outlook-event/OutlookExistingEvent";
import { OutlookNewEvent } from "../components/outlook-new-event/OutlookNewEvent";
import WebLayout from "../components/web-layout/WebLayout";
import AccountSettingsPage from "../pages/AccountSettingsPage";
import PlannerPage from "../pages/PlannerPage";
import WebLandingPage from "../pages/WebLandingPage";
import { m365Nonce } from "../state/local-storage";
import { logger } from "../util/logger.util";
import { getComponentOrRedirect, requireExtensionStoreRedirect } from "./routes.util";

//////////////////
// ROUTE CONFIG
//////////////////
const subRootRoutes = (
  <>
    <Route Component={HomeWrapper}>
      <Route path={paths.uninstall} Component={HomeUninstall} />

      <Route path={`${paths.unsubscribe}/:email/:token`} Component={UnsubscribeWrapperPage} />
      <Route
        path={`${paths.m365SignIn}/:redirect?`}
        getComponent={(props) => getComponentOrRedirect(LoginWrapper, requireNoAuthApp, props)}
        render={({ Component, props }) => {
          return Component ? <Component {...props} variant="m365" /> : null;
        }}
      />
    </Route>
    <Route
      path={webappPathRoot}
      getComponent={(props) => getComponentOrRedirect(WebLayout, requireLoginOutlook, props)}
      data={{ withToolbar: true }}
    >
      <Route path={pagePaths.landing}>
        {{
          secondary: <Route path="(.*)?" Component={AppNav} />,
          children: <Route path="/" Component={WebLandingPage} />,
        }}
      </Route>
      <Route path={pagePaths.preferences}>
        {{
          secondary: <Route path="(.*)?" Component={AppNav} />,
          children: <Route path="/" Component={AccountSettingsPage} />,
        }}
      </Route>
    </Route>
    <Route
      path={webappPathRoot}
      getComponent={(props) => getComponentOrRedirect(WebLayout, requireLoginOutlook, props)}
      data={{ withToolbar: false }}
    >
      <Route path={pagePaths.planner}>
        {{
          secondary: <Route path="(.*)?" Component={AppNav} />,
          children: <Route path="/" Component={PlannerPage} />,
        }}
      </Route>
    </Route>

    <Redirect from={paths.calendar} to={appPaths.planner} />
  </>
);

/*  
  Because routes-outlook is loaded by outlook.tsx which is loaded from /outlook.html which is loaded from /outlook/*,
  Outlook routes need to be redirected to a webapp route in order to use the current url structure we have today.
  Could route all /outlook/* to /app/* but just doing these few manually for now. 
  Only anticipate needing to do this for a few routes.
*/
const outlookToWebAppRedirects = (
  <>
    <Redirect from={paths.login} to={paths.m365SignIn} />
    <Redirect from={outlookPaths.m365SignIn} to={paths.m365SignIn} />
    <Redirect from={outlookPaths.planner} to={paths.planner} />
    <Route
      path={outlookPaths.authRedirect}
      getData={() => {
        const m365AuthRedirectUrl = new URLSearchParams(window.location.search).get("redirectUrl");
        const maybeNonce = new URLSearchParams(window.location.search).get(m365NonceUrlParam);

        // We need to set the nonce to localStorage so that MicrosoftAuthRedirect can read it for Safari and Safari doesn't allow
        // the Outlook auth modal from reading localStorage set by the parent window. /m365 login page sets the nonce in localStorage from the parent window.
        if (maybeNonce) {
          m365Nonce.set(maybeNonce);
        } else {
          logger.error(
            "No nonce provided as URL param for auth redirect route /outlook/microsoft/connect",
          );
        }

        // Outlook Add-in requires us to open all new dialogs to the same host as the add in (Clockwise) and
        // to then redirect to the external location of our choice. So for msft auth within Outlook dialog we redirect to the auth url.
        if (m365AuthRedirectUrl) {
          window.location.replace(m365AuthRedirectUrl);
        }
        logger.error("No redirect url provided for auth redirect route /outlook/microsoft/connect");
      }}
    />
  </>
);

export const outlookRouteConfig = makeRouteConfig(
  <>
    {outlookToWebAppRedirects}
    <Route path="/" Component={ModernWrapperPage}>
      {subRootRoutes}
      <Route
        path={outlookPaths.event}
        getComponent={(props) =>
          getComponentOrRedirect(OutlookEventRouter, requireLoginOutlook, props)
        }
      />
      <Route
        path={outlookPaths.existingEvent}
        getComponent={(props) =>
          getComponentOrRedirect(OutlookExistingEvent, requireLoginOutlook, props)
        }
      />
      <Route
        path={outlookPaths.newEvent}
        getComponent={(props) =>
          getComponentOrRedirect(OutlookNewEvent, requireLoginOutlook, props)
        }
      />
    </Route>
    <Route path="/test-:mainBranch" Component={ModernWrapperPage}>
      {subRootRoutes}
    </Route>

    {/* Routes that don't need the top-level ModernWrapperQuery run*/}
    <Route path={paths.microsoftAuthRedirect} Component={MicrosoftAuthRedirect} />
    <Route
      path={`${paths.invite}/:inviteToken?`}
      getComponent={(props) => requireExtensionStoreRedirect(props, Null, paths.signup, false)}
    />
  </>,
);
